import { CedeSDKError } from "./base";
import { SdkErrorCodes, SdkErrorMessages } from "./errorsMetadata";

/**
 * @description Default error for exchange
 */
export class ExchangeError extends CedeSDKError {
  constructor(code = SdkErrorCodes.EXCHANGE_ERROR, message = SdkErrorMessages.EXCHANGE_ERROR) {
    super(code, message);
    this.name = "ExchangeError";
  }
}

/**
 * @description Thrown when the exchange server status is offline
 */
export class ExchangeServerDownError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_SERVER_DOWN, SdkErrorMessages.EXCHANGE_SERVER_DOWN);
    this.name = "ExchangeServerDownError";
  }
}

/**
 * @description Thrown when the exchange server status is under maintenance
 */
export class ExchangeUnderMaintenanceError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_UNDER_MAINTENANCE, SdkErrorMessages.EXCHANGE_UNDER_MAINTENANCE);
    this.name = "ExchangeUnderMaintenanceError";
  }
}

/**
 * @description Thrown when the request has been expired by the throttling mechanism
 */
export class ExchangeRequestExpiredError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_REQUEST_EXPIRED, SdkErrorMessages.EXCHANGE_REQUEST_EXPIRED);
    this.name = "ExchangeRequestExpiredError";
  }
}

/**
 * @description Thrown when the exchange api can't perform the request due to missing / invalid permissions
 */
export class ExchangeInvalidPermissionsError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_INVALID_PERMISSION, SdkErrorMessages.EXCHANGE_INVALID_PERMISSION);
    this.name = "ExchangeInvalidPermissionsError";
  }
}

// TODO: Replace it by ExchangeFeatureNotAllowedError
export class ExchangeTransferNotAllowedError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_TRANSFER_NOT_ALLOWED, SdkErrorMessages.EXCHANGE_TRANSFER_NOT_ALLOWED);
    this.name = "ExchangeTransferNotAllowedError";
  }
}

export class ExchangeTransferError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_TRANSFER_ERROR, SdkErrorMessages.EXCHANGE_TRANSFER_ERROR);
    this.name = "ExchangeTransferError";
  }
}

export class ExchangeDDosProtectionError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_DDOS_PROTECTION, SdkErrorMessages.EXCHANGE_DDOS_PROTECTION);
    this.name = "ExchangeDDosProtectionError";
  }
}

export class ExchangeRateLimitExceededError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_RATE_LIMIT_EXCEEDED, SdkErrorMessages.EXCHANGE_RATE_LIMIT_EXCEEDED);
    this.name = "ExchangeRateLimitExceededError";
  }
}

export class ExchangeOperationFailedError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_OPERATION_FAILED, SdkErrorMessages.EXCHANGE_OPERATION_FAILED);
    this.name = "ExchangeOperationFailedError";
  }
}

/**
 * @description Thrown when an order's position side doesn't match the account's position mode
 * (Binance USDⓈ-M -4061): a one-way order sent to a hedge-mode account, or vice versa. Signals
 * that a cached position mode is stale and should be refetched.
 */
export class ExchangePositionSideMismatchError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_POSITION_SIDE_MISMATCH, SdkErrorMessages.EXCHANGE_POSITION_SIDE_MISMATCH);
    this.name = "ExchangePositionSideMismatchError";
  }
}

/**
 * @description Thrown when the API key lacks the permission to read the account's trade history
 */
export class ExchangeMissingTradeHistoryPermissionError extends ExchangeError {
  constructor() {
    super(
      SdkErrorCodes.EXCHANGE_MISSING_TRADE_HISTORY_PERMISSION,
      SdkErrorMessages.EXCHANGE_MISSING_TRADE_HISTORY_PERMISSION,
    );
    this.name = "ExchangeMissingTradeHistoryPermissionError";
  }
}

export class ExchangeUnknownAssetError extends ExchangeError {
  constructor(asset: string) {
    super(SdkErrorCodes.EXCHANGE_UNKNOWN_ASSET, SdkErrorMessages.EXCHANGE_UNKNOWN_ASSET(asset));
    this.name = "ExchangeUnknownAssetError";
  }
}

export class ExchangeAccountSuspendedError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_ACCOUNT_SUSPENDED, SdkErrorMessages.EXCHANGE_ACCOUNT_SUSPENDED);
    this.name = "ExchangeAccountSuspendedError";
  }
}

/**
 * @description Thrown when the method used is not allowed by the exchange
 */
export class ExchangeNotAllowedMethodError extends ExchangeError {
  constructor(methodName: string) {
    super(SdkErrorCodes.EXCHANGE_NOT_ALLOWED_METHOD, SdkErrorMessages.EXCHANGE_NOT_ALLOWED_METHOD(methodName));
    this.name = "ExchangeNotAllowedMethodError";
  }
}

export class ExchangeNotAvailableError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_NOT_AVAILABLE, SdkErrorMessages.EXCHANGE_NOT_AVAILABLE);
    this.name = "ExchangeNotAvailableError";
  }
}

export class ExchangeInvalidNonceError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_INVALID_NONCE, SdkErrorMessages.EXCHANGE_INVALID_NONCE);
    this.name = "ExchangeInvalidNonceError";
  }
}

export class ExchangeUnavailableInCountry extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.EXCHANGE_UNAVAILABLE_IN_COUNTRY, SdkErrorMessages.EXCHANGE_UNAVAILABLE_IN_COUNTRY);
    this.name = "ExchangeUnavailableInCountry";
  }
}

export class ExchangeWalletTypeNotSupportedError extends ExchangeError {
  constructor(walletType: string) {
    super(
      SdkErrorCodes.EXCHANGE_WALLET_TYPE_NOT_SUPPORTED,
      SdkErrorMessages.EXCHANGE_WALLET_TYPE_NOT_SUPPORTED(walletType),
    );
    this.name = "ExchangeWalletTypeNotSupportedError";
  }
}

export class ExchangeOperationNotAllowedForSubAccountError extends ExchangeError {
  constructor() {
    super(
      SdkErrorCodes.EXCHANGE_OPERATION_NOT_ALLOWED_FOR_SUB_ACCOUNT,
      SdkErrorMessages.EXCHANGE_OPERATION_NOT_ALLOWED_FOR_SUB_ACCOUNT,
    );
    this.name = "ExchangeOperationNotAllowedForSubAccountError";
  }
}

export class KyCError extends ExchangeError {
  constructor() {
    super(SdkErrorCodes.KYC_ERROR, SdkErrorMessages.KYC_ERROR);
    this.name = "KyCError";
  }
}
